import React, { useState } from 'react';
import { AlertTriangle } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useT } from '@/contexts/I18nContext';
import { cn } from '@/lib/utils';

import {
  getLinkTimeRange,
  setLinkTimeRange,
  mskHhmmToMs,
  msToMskHhmm,
  type TypedLine,
} from './lineModel';
import { defFor } from './typedLines';

function previewHtml(row: string): string {
  return row.replace(/<a\s+/gi, '<a target="_blank" rel="noreferrer" ');
}

// Grafana checkout link padding around the peak window (matches the generator's 15min).
const PEAK_PAD_MS = 15 * 60 * 1000;

/**
 * Renders the template form for a typed queue line. `q_longest` is special: a single
 * "peak time" drives both the "спала за X мин" and the Самая длинная link window
 * ([peak−15m, peak+drain+15m]) so the two can't disagree. `q_longqueue` warns when the
 * branch has ≤1 cashier but the "opened a second cashier" box is ticked.
 */
export function TypedLineForm({
  line,
  day,
  maxCashiers,
  onApply,
  onCancel,
  hidePreview,
}: {
  line: TypedLine;
  day: string;
  maxCashiers?: number | null;
  onApply: (row: string) => void;
  onCancel: () => void;
  /** item 7 (round-2, Alexey 2026-09): the Интерактив queue card already
   *  shows this line's full rendered text once, at the top of the card
   *  (D1's "whole text first" pattern) — this form's OWN preview box below
   *  duplicated that same line a second time. Constructor's click-to-edit
   *  flow (no top summary there) still wants it, so this is opt-in, not a
   *  removal. */
  hidePreview?: boolean;
}) {
  const { t } = useT();
  const def = defFor(line.typedKind);
  const [fields, setFields] = useState({ ...line.fields });
  const [meta, setMeta] = useState({ ...line.meta });

  const isLongest = line.typedKind === 'q_longest';
  const baseHref = line.meta.href ?? '';
  const initFrom = baseHref ? getLinkTimeRange(baseHref).fromMs : null;
  const [peakTime, setPeakTime] = useState(
    initFrom != null ? msToMskHhmm(initFrom + PEAK_PAD_MS) : '',
  );

  const set = (key: string, v: number | string | boolean) =>
    setFields((prev) => ({ ...prev, [key]: v }));

  // q_longest: recompute the link window from the peak time + drain minutes.
  const syncLongestLink = (peak: string, minutes: number) => {
    if (!baseHref) return;
    const peakMs = mskHhmmToMs(day, peak);
    if (peakMs == null) return;
    const from = peakMs - PEAK_PAD_MS;
    const to = peakMs + Math.max(0, minutes) * 60_000 + PEAK_PAD_MS;
    setMeta((prev) => ({ ...prev, href: setLinkTimeRange(baseHref, from, to) }));
  };

  const preview = def.serialize(fields, meta);
  const showCashierWarn =
    line.typedKind === 'q_longqueue' &&
    Boolean(fields.secondOn) &&
    maxCashiers != null &&
    maxCashiers <= 1;

  return (
    <div className="flex flex-col gap-3">
      <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
        {t(def.titleKey)}
      </div>

      {isLongest && baseHref && (
        <label className="flex flex-col gap-1 text-sm">
          <span className="text-muted-foreground">{t('reportValidation.tf.peakTime')}</span>
          <Input
            type="time"
            value={peakTime}
            onChange={(e) => {
              setPeakTime(e.target.value);
              syncLongestLink(e.target.value, Number(fields.minutes));
            }}
            className="w-32"
          />
        </label>
      )}

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        {def.fields.map((spec) => {
          if (spec.showIf && !spec.showIf(fields)) return null;
          if (spec.kind === 'toggle') {
            return (
              <label key={spec.key} className="flex items-center gap-2 text-sm sm:col-span-2">
                <input
                  type="checkbox"
                  checked={Boolean(fields[spec.key])}
                  onChange={(e) => set(spec.key, e.target.checked)}
                  className="size-4"
                />
                <span className="text-muted-foreground">{t(spec.labelKey)}</span>
              </label>
            );
          }
          return (
            <label key={spec.key} className="flex flex-col gap-1 text-sm">
              <span className="text-muted-foreground">{t(spec.labelKey)}</span>
              <Input
                type={spec.kind === 'time' ? 'time' : 'number'}
                min={spec.kind === 'percent' ? 0 : undefined}
                max={spec.kind === 'percent' ? 100 : undefined}
                value={String(fields[spec.key] ?? '')}
                onChange={(e) => {
                  const v = spec.kind === 'time' ? e.target.value : Number(e.target.value);
                  set(spec.key, v);
                  if (isLongest && spec.key === 'minutes') syncLongestLink(peakTime, Number(v));
                }}
                className={cn('w-auto', spec.kind === 'time' && 'w-32')}
              />
            </label>
          );
        })}
      </div>

      {showCashierWarn && (
        <div className="flex items-start gap-2 rounded-md border border-amber-400/50 bg-amber-400/10 p-2 text-xs text-amber-700 dark:text-amber-400">
          <AlertTriangle className="mt-0.5 size-3.5 shrink-0" />
          <span>{t('reportValidation.tf.oneCashierWarn')}</span>
        </div>
      )}

      {!hidePreview && (
        <div className="flex flex-col gap-1">
          <span className="text-xs text-muted-foreground">{t('reportValidation.form.preview')}</span>
          <div
            className="report-body rounded-md border border-border bg-muted/40 p-2 text-sm"
            dangerouslySetInnerHTML={{ __html: previewHtml(preview) }}
          />
        </div>
      )}

      <div className="flex justify-end gap-2">
        <Button type="button" variant="ghost" size="sm" onClick={onCancel}>
          {t('reportValidation.form.cancel')}
        </Button>
        <Button type="button" size="sm" onClick={() => onApply(preview)}>
          {t('reportValidation.form.apply')}
        </Button>
      </div>
    </div>
  );
}
